import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, inputCls, PasswordInput } from "@/components/common";
import { useMeta } from "@/hooks/useMeta";

const Shell = ({ title, sub, children }: any) => (
  <div className="max-w-md mx-auto px-5 py-20 md:py-28">
    <h1 className="font-display text-4xl sm:text-5xl font-light">{title}</h1>
    {sub && <p className="mt-4 text-sm text-neutral-600">{sub}</p>}
    <div className="mt-10">{children}</div>
  </div>
);

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [err, setErr] = useState("");
  useMeta("Sign In | Somnio.Co");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr("");
    try { const u = await login(f.email, f.password); nav(params.get("next") || (u.role === "admin" ? "/admin" : "/account")); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <Shell title="Sign In" sub="Access your orders, wishlist and saved designs.">
      <form onSubmit={submit} className="space-y-6" data-testid="login-form">
        <Field label="Email"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="login-email-input" /></Field>
        <Field label="Password"><PasswordInput required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testId="login-password-input" /></Field>
        {err && <p className="text-sm text-red-800" data-testid="login-error">{err}</p>}
        <button className="btn-ink w-full py-4 text-xs tracking-[0.25em] uppercase" data-testid="login-submit-button">Sign In</button>
        <div className="flex justify-between text-sm"><Link to="/forgot-password" className="underline" data-testid="forgot-password-link">Forgot password?</Link><Link to="/register" className="underline" data-testid="register-link">Create account</Link></div>
      </form>
    </Shell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  useMeta("Create Account | Somnio.Co");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr("");
    try { await register(f.name, f.email, f.password); toast("Welcome to Somnio.Co"); nav(params.get("next") || "/account"); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <Shell title="Create Account" sub="Save pieces, track orders and book consultations.">
      <form onSubmit={submit} className="space-y-6" data-testid="register-form">
        <Field label="Full name"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="register-name-input" /></Field>
        <Field label="Email"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="register-email-input" /></Field>
        <Field label="Password (min 8 characters)"><PasswordInput required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} testId="register-password-input" /></Field>
        {err && <p className="text-sm text-red-800" data-testid="register-error">{err}</p>}
        <button className="btn-ink w-full py-4 text-xs tracking-[0.25em] uppercase" data-testid="register-submit-button">Create Account</button>
        <p className="text-sm">Already have an account? <Link to={`/login${params.get("next") ? `?next=${encodeURIComponent(params.get("next")!)}` : ""}`} className="underline">Sign in</Link></p>
      </form>
    </Shell>
  );
}

export function ForgotPassword() {
  useMeta("Reset your password");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e: React.FormEvent) => { e.preventDefault(); await api.post("/auth/forgot-password", { email }).catch(() => {}); setSent(true); };
  return (
    <Shell title="Reset Password" sub="We'll email you a secure link to choose a new password.">
      {sent ? <p className="text-sm" data-testid="forgot-sent">If an account exists for {email}, a reset link is on its way.</p> : (
        <form onSubmit={submit} className="space-y-6" data-testid="forgot-form">
          <Field label="Email"><input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="forgot-email-input" /></Field>
          <button className="btn-ink w-full py-4 text-xs tracking-[0.25em] uppercase" data-testid="forgot-submit-button">Send Link</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  useMeta("Choose a new password");
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: params.get("token"), password: pw }); toast("Password updated. Please sign in."); nav("/login"); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <Shell title="Choose a New Password">
      <form onSubmit={submit} className="space-y-6" data-testid="reset-form">
        <Field label="New password"><PasswordInput required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} testId="reset-password-input" /></Field>
        {err && <p className="text-sm text-red-800" data-testid="reset-error">{err}</p>}
        <button className="btn-ink w-full py-4 text-xs tracking-[0.25em] uppercase" data-testid="reset-submit-button">Update Password</button>
      </form>
    </Shell>
  );
}
